import { useMemo } from 'react';
import { Feature, FeatureCollection } from 'geojson';
import { FilterSpecification, Layer, Source } from 'shared/map-exports';
import { Alert } from 'features/alerts/alerts.types';
import { BELOW_STREETS_LAYER_ID } from 'features/mapping/styles';
import { useMapLayersState } from 'state/useMapLayersState';
import {
  getFillStyle,
  getPatternFillStyle,
  getStrokeStyle,
  getWeaFillStyle,
  getWeaPatternFillStyle,
  getWeaStrokeStyle,
} from './NWSWatchesAndWarningsLayer.styles';

const SOURCE_ID = 'single-warning-or-alert-source';
const FILL_LAYER_ID = 'single-warning-or-alert-fill';
const PATTERN_LAYER_ID = 'single-warning-or-alert-pattern';
const STROKE_LAYER_ID = 'single-warning-or-alert-stroke';

type SingleWarningsAndAlertsLayerProps = {
  alert: Alert;
};

export const SingleWarningsAndAlertsLayer = (
  props: SingleWarningsAndAlertsLayerProps,
): JSX.Element => {
  const { alert } = props;
  const { isDarkBaseLayer } = useMapLayersState();

  // Mirror the tile-source feature shape so the shared style filters
  // (isFill, isExteriorShell, type/subtype) match this single feature.
  const geojson: FeatureCollection = useMemo(() => {
    const feature: Feature = {
      type: 'Feature',
      id: alert.id,
      geometry: alert.geom,
      properties: {
        type: alert.type,
        subtype: alert.subtype,
        is_fill: true,
        is_exterior_shell: true,
      },
    };
    return { type: 'FeatureCollection', features: [feature] };
  }, [alert.id, alert.geom, alert.type, alert.subtype]);

  // always a single feature, so a no-op `['all']` filter
  const matchAll: FilterSpecification = ['all'];

  const isWea = alert.type === 'wea';
  const fillStyle = isWea
    ? getWeaFillStyle(true)
    : getFillStyle(matchAll, isDarkBaseLayer);
  const patternStyle = isWea
    ? getWeaPatternFillStyle(true)
    : getPatternFillStyle(matchAll, isDarkBaseLayer);
  const strokeStyle = isWea
    ? getWeaStrokeStyle(true)
    : getStrokeStyle(matchAll, isDarkBaseLayer);

  return (
    <Source id={SOURCE_ID} type="geojson" data={geojson}>
      <Layer
        id={FILL_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...fillStyle}
      />
      <Layer
        id={PATTERN_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...patternStyle}
      />
      <Layer
        id={STROKE_LAYER_ID}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...strokeStyle}
      />
    </Source>
  );
};
